import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  distributeEmail,
  getProgram,
  listDeliveries,
  previewEmailSend,
  retryFailedEmails,
  type Batch,
  type EmailSendCounts,
  type RetryFailedResult,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { useConfirmFocus } from "@/components/confirmFocus";
import { ErrorBanner, Metric, ToolbarButton } from "@/components/ui";
import { answeredAfterKeyCheck, emailAttempts } from "@/features/batches/attempts";
import { useEmailTemplateChoices } from "@/features/email/choices";

export const EMAIL_POLL_MS = 3_000;

export const emailCountsKey = (batchId: string) => ["deliveries", { batchId, channel: "email", counts: true }] as const;

const fieldClass = "mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

type Confirming = "send" | "retry" | null;

export function EmailPanel({ batch }: { batch: Batch }) {
  const { can } = useSession();
  const canSend = can("certificate.send");
  const queryClient = useQueryClient();
  const counts = useQuery({
    queryKey: emailCountsKey(batch.id),
    queryFn: () => listDeliveries({ batchId: batch.id, channel: "email", limit: 1 }),
    refetchInterval: (q) => {
      const email = q.state.data?.counts?.email;
      // A failing request would otherwise be retried every few seconds forever.
      if (q.state.status === "error") return false;
      return email && email.queued + email.sending > 0 ? EMAIL_POLL_MS : false;
    },
  });
  const email = counts.data?.counts?.email;
  const polling = !counts.isError && !!email && email.queued + email.sending > 0;
  const total = email ? email.queued + email.sending + email.sent + email.failed : 0;
  // The batch moves from Distributing to Completed once the last email settles.
  const active = email ? email.queued + email.sending > 0 : null;
  const wasActive = useRef(false);
  useEffect(() => {
    if (active === null) return;
    if (wasActive.current && !active) void queryClient.invalidateQueries({ queryKey: ["batch", batch.id] });
    wasActive.current = active;
  }, [active, batch.id, queryClient]);

  const choices = useEmailTemplateChoices();
  const live = choices.templates ?? [];
  const programEnabled = canSend && !!batch.program_id && can("program.read");
  const program = useQuery({
    queryKey: ["program", batch.program_id],
    queryFn: () => getProgram(batch.program_id as string),
    enabled: programEnabled,
  });
  // Wait for the program so the select doesn't jump from the default to the program's template.
  const programPending = programEnabled && program.isPending;
  const preferred = live.find((t) => t.id === program.data?.email_template_id) ?? live.find((t) => t.is_default);
  const defaultId = programPending ? "" : (preferred?.id ?? "");
  const [chosen, setChosen] = useState<string | null>(null);
  // A choice archived since it was picked falls back to the default.
  const templateId = chosen && live.some((t) => t.id === chosen) ? chosen : defaultId;

  const [confirming, setConfirming] = useState<Confirming>(null);
  // Send mails only the template the dry run counted, whatever a refetch changes underneath.
  const [reviewedId, setReviewedId] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [sent, setSent] = useState<(EmailSendCounts & { replayed: boolean }) | null>(null);
  const [retried, setRetried] = useState<RetryFailedResult | null>(null);
  const sendFocus = useConfirmFocus(confirming === "send");
  const retryFocus = useConfirmFocus(confirming === "retry");
  // isPending lands on the next render; a second click in the same tick must not send twice.
  const submitting = useRef(false);
  const retrying = useRef(false);

  const refresh = async () => {
    // Every deliveries query: the counts here and any open /deliveries list.
    await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    await queryClient.invalidateQueries({ queryKey: ["batch", batch.id] });
  };
  const preview = useMutation({
    mutationFn: (id: string) => previewEmailSend(batch.id, id),
  });
  const distribute = useMutation({
    // The same template after a lost answer, even across a reload, replays the first send instead of mailing twice.
    mutationFn: (id: string) => distributeEmail(batch.id, id, emailAttempts.keyFor(batch.id, id)),
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: async (result) => {
      emailAttempts.forget(batch.id);
      setConfirming(null);
      setSent(result);
      await refresh();
    },
    onError: async (error) => {
      const nothingMade = answeredAfterKeyCheck(error);
      if (nothingMade) emailAttempts.forget(batch.id);
      setConfirming(null);
      if (!nothingMade) await refresh();
    },
  });
  const retry = useMutation({
    mutationFn: () => retryFailedEmails(batch.id),
    onSettled: async () => {
      retrying.current = false;
      setConfirming(null);
      await refresh();
    },
    onSuccess: (result) => setRetried(result),
  });
  // Once the send is in flight it is the reviewed template that goes; the outcome closes the confirm.
  if (confirming === "send" && reviewedId !== null && templateId !== reviewedId && !distribute.isPending) {
    setConfirming(null);
    setReviewedId(null);
    setStale(true);
  }
  const begin = () => {
    preview.reset();
    distribute.reset();
    retry.reset();
    setSent(null);
    setRetried(null);
    setStale(false);
  };
  const busy = preview.isPending || distribute.isPending || retry.isPending;

  const dryRun = preview.data;
  const failedSearch = { batch: batch.id, status: "failed" } as const;
  const noTemplates = choices.templates !== null && !choices.isLoading && !choices.error && live.length === 0;

  return (
    <section className="space-y-3 rounded-md border border-zinc-200 p-3">
      <h3 className="text-sm font-semibold">Email</h3>
      {email && total > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="Queued" value={email.queued} />
          <Metric label="Sending" value={email.sending} />
          <Metric label="Sent" value={email.sent} />
          <Metric label="Failed" value={email.failed} tone={email.failed > 0 ? "danger" : "default"} />
        </div>
      ) : counts.isSuccess ? (
        <p className="text-xs text-zinc-500">No emails sent for this batch yet.</p>
      ) : null}
      {polling ? (
        <p role="status" className="text-xs text-sky-700">
          Sending…
        </p>
      ) : null}
      {email && email.failed > 0 ? (
        <Link to="/deliveries" search={failedSearch} className="text-sm font-medium text-zinc-950 underline">
          View failed emails
        </Link>
      ) : null}
      <ErrorBanner error={counts.error} />

      {canSend ? (
        <>
          <label className="block text-sm">
            <span className="font-medium text-zinc-700">Email template</span>
            <select
              value={templateId}
              disabled={confirming !== null || busy}
              onChange={(e) => {
                setChosen(e.target.value);
                begin();
              }}
              className={fieldClass}
            >
              {templateId === "" ? <option value="">Choose a template</option> : null}
              {live.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {noTemplates ? <p className="text-xs text-zinc-500">No email templates yet</p> : null}
          <ErrorBanner error={choices.error} />
          <div className="flex flex-wrap gap-2">
            <ToolbarButton
              ref={sendFocus.trigger}
              disabled={!templateId || busy || confirming !== null}
              onClick={() => {
                begin();
                // Per-call callbacks are dropped by reset(), so a superseded dry run never opens the confirm.
                preview.mutate(templateId, {
                  onSuccess: (_, id) => {
                    setReviewedId(id);
                    setConfirming("send");
                  },
                });
              }}
            >
              Review email send
            </ToolbarButton>
            {email && email.retryable > 0 ? (
              <ToolbarButton
                ref={retryFocus.trigger}
                disabled={confirming !== null || busy}
                onClick={() => {
                  begin();
                  setConfirming("retry");
                }}
              >
                Retry {email.retryable} failed
              </ToolbarButton>
            ) : null}
          </div>

          {confirming === "send" && dryRun && reviewedId ? (
            <div role="group" aria-label="Confirm email send" className="space-y-2 rounded-md bg-zinc-50 p-3 text-sm text-zinc-700">
              <ul className="list-disc space-y-1 pl-5">
                <li>{dryRun.queued} to send</li>
                {dryRun.already_sent_or_queued > 0 ? <li>{dryRun.already_sent_or_queued} already sent or queued</li> : null}
                {dryRun.unconfirmed > 0 ? (
                  <li>
                    {dryRun.unconfirmed} not confirmed earlier — retry those one by one in{" "}
                    <Link to="/deliveries" search={failedSearch} className="font-medium text-zinc-950 underline">
                      Deliveries
                    </Link>
                  </li>
                ) : null}
                {dryRun.no_email > 0 ? <li>{dryRun.no_email} have no email address</li> : null}
                {dryRun.revoked > 0 ? <li>{dryRun.revoked} revoked</li> : null}
                {dryRun.failed_render > 0 ? <li>{dryRun.failed_render} certificate not generated</li> : null}
              </ul>
              {dryRun.queued === 0 ? (
                <p role="status" className="font-medium">
                  Nothing new to send.
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {dryRun.queued > 0 ? (
                  <ToolbarButton tone="primary"
                    ref={sendFocus.confirm}
                    disabled={distribute.isPending}
                    onClick={() => {
                      if (submitting.current) return;
                      submitting.current = true;
                      distribute.mutate(reviewedId);
                    }}
                  >
                    {distribute.isPending ? "Sending..." : `Send to ${dryRun.queued} recipients`}
                  </ToolbarButton>
                ) : null}
                <ToolbarButton
                  ref={dryRun.queued === 0 ? sendFocus.confirm : undefined}
                  disabled={distribute.isPending}
                  onClick={() => setConfirming(null)}
                >
                  Cancel
                </ToolbarButton>
              </div>
            </div>
          ) : null}

          {confirming === "retry" && email ? (
            <div role="group" aria-label="Confirm retry failed" className="space-y-2 rounded-md bg-zinc-50 p-3 text-sm text-zinc-700">
              <p>
                Retry {email.retryable} failed emails? Emails whose send was not confirmed are skipped; retry those one by one.
              </p>
              <div className="flex flex-wrap gap-2">
                <ToolbarButton
                  ref={retryFocus.confirm}
                  disabled={retry.isPending}
                  onClick={() => {
                    if (retrying.current) return;
                    retrying.current = true;
                    retry.mutate();
                  }}
                >
                  {retry.isPending ? "Retrying..." : "Retry failed emails"}
                </ToolbarButton>
                <ToolbarButton disabled={retry.isPending} onClick={() => setConfirming(null)}>
                  Cancel
                </ToolbarButton>
              </div>
            </div>
          ) : null}

          {stale ? (
            <p role="status" className="text-sm text-amber-800">
              The template changed; review the send again.
            </p>
          ) : null}
          {sent?.replayed ? (
            <p role="status" className="text-sm text-amber-800">
              This send had already started; nothing new was queued. Its progress is shown below.
            </p>
          ) : sent ? (
            <p role="status" className="text-sm text-emerald-700">
              Queued {sent.queued} emails.
            </p>
          ) : null}
          {retried ? (
            <p role="status" className="text-sm text-emerald-700">
              Requeued {retried.requeued}.
              {retried.skipped_unconfirmed > 0 ? ` ${retried.skipped_unconfirmed} not confirmed were skipped.` : ""}
            </p>
          ) : null}
          <ErrorBanner error={preview.error ?? distribute.error ?? retry.error} />
        </>
      ) : null}
    </section>
  );
}
